// CTSK-367 — inline editor for one Data Sources row. Mirrors the
// CTSK-364 BookmarkRowEditor pattern (same PATCH endpoint, same
// "parent owns one-open-at-a-time", same null-deletes-cleared-
// optionals semantics), adapted for the data-sources-v1 discriminated
// union (CTSK-303): ONE component whose field-set branches on
// `entry.kind` (Path A per the ticket).
//
// Contract:
//   - `kind` is NOT editable — converting between variants is a
//     re-create, not an edit. It renders as a read-only line.
//   - Machine-stamped fields stay read-only: id (both variants),
//     installed_by / installed_at / known_producers (internal —
//     manifest-install/backfill provenance, not operator data).
//   - internal-registry: schemaRef {documentId, url, label} is
//     editable as three sub-fields (closes the CTSK-366 loop — the
//     chip can now be populated in-UI). All three cleared → PATCH
//     sends `schemaRef: null` (server deletes the key; no empty-husk
//     `schemaRef: {}` rows).
//   - external-url: the operator-authored field set is editable;
//     select-backed vocab fields (type/status/update_frequency/
//     subscription) include the entry's CURRENT value even when it's
//     off-vocabulary (real entries vary in case/vintage — the editor
//     must never silently rewrite a value the operator didn't touch).
//   - Save PATCHes the kind-appropriate subset and reports the merged
//     entry via onSaved; the parent refetches the YAML (persisted
//     bytes are the source of truth, no optimistic state).

import { useMemo, useState } from 'react';
import { patchRegistryEntry, ApiError } from '../lib/api.js';
import { toast } from './Toast.js';

/** Structural subset of DataSourcesTable's DataSource the editor
 *  needs. Kept structural (not imported) so the editor stays
 *  decoupled from the table's render-side type — same division as
 *  BookmarkRowEditor.EditableBookmark. */
export interface EditableDataSource {
  kind?: 'external-url' | 'internal-registry';
  id: string;
  name: string;
  type?: string;
  status?: string;
  description?: string;
  url?: string;
  owner?: { name?: string; email?: string };
  update_frequency?: string;
  subscription?: string;
  license?: string;
  cost_annual_usd?: number | null;
  tools_used?: string[];
  tags?: string[];
  last_reviewed?: string;
  notes?: string;
  // internal-registry
  target_tool?: string;
  target_file?: string;
  endpoint?: string;
  direction?: 'inbound' | 'bidirectional';
  known_producers?: string[];
  installed_by?: string;
  installed_at?: string;
  schemaRef?: { documentId?: string; url?: string; label?: string };
}

export interface DataSourceRowEditorProps {
  source: EditableDataSource;
  toolId: string;
  filePath: string;
  onSaved: (entry: Record<string, unknown>) => void;
  onCancel: () => void;
}

// Vocab lists mirror DataSourcesTable's (duplicated by design — the
// table's are private; the schema keeps these plain strings so vocab
// can grow without a deploy, hence withCurrent() below).
const SOURCE_TYPES = ['api', 'database', 'file_feed', 'scraper', 'dataset', 'stream', 'sdk', 'manual', 'other'];
const SOURCE_STATUSES = ['active', 'evaluating', 'deprecated', 'archived'];
const UPDATE_FREQUENCIES = ['realtime', 'hourly', 'daily', 'weekly', 'monthly', 'quarterly', 'annual', 'ad_hoc', 'unknown'];
const SUBSCRIPTIONS = ['free', 'freemium', 'paid', 'trial', 'internal', 'open_source'];

/** Vocab options + the entry's current (possibly off-vocabulary)
 *  value, so opening the editor never coerces an untouched field. */
function withCurrent(options: string[], current: string | undefined): string[] {
  if (!current || options.includes(current)) return options;
  return [current, ...options];
}

function splitCsv(text: string): string[] {
  const seen = new Set<string>();
  return text.split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0 && !seen.has(t) && (seen.add(t) || true));
}

/** '' → null (delete the optional field server-side), else trimmed. */
function orNull(text: string): string | null {
  const t = text.trim();
  return t.length > 0 ? t : null;
}

function validUrl(text: string): boolean {
  try {
    new URL(text);
    return true;
  } catch {
    return false;
  }
}

export function DataSourceRowEditor({
  source,
  toolId,
  filePath,
  onSaved,
  onCancel,
}: DataSourceRowEditorProps) {
  const isInternal = source.kind === 'internal-registry';

  // Common
  const [name, setName] = useState(source.name ?? '');
  const [description, setDescription] = useState(source.description ?? '');
  const [status, setStatus] = useState(source.status ?? '');
  const [tagsText, setTagsText] = useState((source.tags ?? []).join(', '));
  const [notes, setNotes] = useState(source.notes ?? '');
  // External-url
  const [type, setType] = useState(source.type ?? '');
  const [url, setUrl] = useState(source.url ?? '');
  const [ownerName, setOwnerName] = useState(source.owner?.name ?? '');
  const [ownerEmail, setOwnerEmail] = useState(source.owner?.email ?? '');
  const [updateFrequency, setUpdateFrequency] = useState(source.update_frequency ?? '');
  const [subscription, setSubscription] = useState(source.subscription ?? '');
  const [license, setLicense] = useState(source.license ?? '');
  const [costText, setCostText] = useState(source.cost_annual_usd == null ? '' : String(source.cost_annual_usd));
  const [toolsUsedText, setToolsUsedText] = useState((source.tools_used ?? []).join(', '));
  const [lastReviewed, setLastReviewed] = useState(source.last_reviewed ?? '');
  // Internal-registry
  const [targetTool, setTargetTool] = useState(source.target_tool ?? '');
  const [targetFile, setTargetFile] = useState(source.target_file ?? '');
  const [endpoint, setEndpoint] = useState(source.endpoint ?? '');
  const [direction, setDirection] = useState<'inbound' | 'bidirectional'>(source.direction ?? 'inbound');
  const [schemaRefDocId, setSchemaRefDocId] = useState(source.schemaRef?.documentId ?? '');
  const [schemaRefUrl, setSchemaRefUrl] = useState(source.schemaRef?.url ?? '');
  const [schemaRefLabel, setSchemaRefLabel] = useState(source.schemaRef?.label ?? '');

  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Field-level validation mirrors the variant's schema rules; the
  // server still validates the MERGED entry as the source of truth.
  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    if (name.trim().length === 0) out.name = 'Name is required';
    if (isInternal) {
      if (targetTool.trim().length === 0) out.target_tool = 'Target tool is required';
      if (targetFile.trim().length === 0) out.target_file = 'Target file is required';
      if (endpoint.trim().length === 0) out.endpoint = 'Endpoint is required';
      if (schemaRefUrl.trim().length > 0 && !validUrl(schemaRefUrl.trim())) {
        out.schemaRefUrl = 'Must be a valid URL (https://…)';
      }
    } else {
      if (type.trim().length === 0) out.type = 'Type is required';
      if (status.trim().length === 0) out.status = 'Status is required';
      if (costText.trim().length > 0 && Number.isNaN(Number(costText.trim()))) {
        out.cost = 'Must be a number';
      }
    }
    return out;
  }, [name, isInternal, targetTool, targetFile, endpoint, schemaRefUrl, type, status, costText]);
  const valid = Object.keys(errors).length === 0;

  const buildPatch = (): Record<string, unknown> => {
    const patch: Record<string, unknown> = {
      name: name.trim(),
      description: orNull(description),
      tags: splitCsv(tagsText).length > 0 ? splitCsv(tagsText) : null,
      notes: orNull(notes),
    };
    if (isInternal) {
      patch.status = orNull(status);           // optional on internal
      patch.target_tool = targetTool.trim();
      patch.target_file = targetFile.trim();
      patch.endpoint = endpoint.trim();
      patch.direction = direction;
      const schemaRef: Record<string, string> = {};
      if (schemaRefDocId.trim()) schemaRef.documentId = schemaRefDocId.trim();
      if (schemaRefUrl.trim()) schemaRef.url = schemaRefUrl.trim();
      if (schemaRefLabel.trim()) schemaRef.label = schemaRefLabel.trim();
      patch.schemaRef = Object.keys(schemaRef).length > 0 ? schemaRef : null;
    } else {
      patch.status = status.trim();            // required on external
      patch.type = type.trim();
      patch.url = orNull(url);
      const owner: Record<string, string> = {};
      if (ownerName.trim()) owner.name = ownerName.trim();
      if (ownerEmail.trim()) owner.email = ownerEmail.trim();
      patch.owner = Object.keys(owner).length > 0 ? owner : null;
      patch.update_frequency = orNull(updateFrequency);
      patch.subscription = orNull(subscription);
      patch.license = orNull(license);
      patch.cost_annual_usd = costText.trim().length > 0 ? Number(costText.trim()) : null;
      patch.tools_used = splitCsv(toolsUsedText).length > 0 ? splitCsv(toolsUsedText) : null;
      patch.last_reviewed = orNull(lastReviewed);
    }
    return patch;
  };

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setServerError(null);
    try {
      const res = await patchRegistryEntry(toolId, filePath, source.id, buildPatch());
      toast.success(`Source '${source.id}' saved`);
      onSaved(res.entry);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : (err as Error).message;
      setServerError(message);
      toast.error(`Save failed: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="form"
      aria-label={`Edit data source ${source.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '10px 16px',
        padding: '12px 14px',
        background: 'color-mix(in srgb, var(--accent-primary) 5%, var(--surface-raised))',
        border: '1px solid color-mix(in srgb, var(--accent-primary) 30%, var(--border-subtle))',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {/* Read-only identity line — kind is a re-create, not an edit. */}
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>kind: <code style={{ fontFamily: 'var(--font-mono)' }}>{isInternal ? 'internal-registry' : 'external-url'}</code> (read-only)</span>
        <span>id: <code style={{ fontFamily: 'var(--font-mono)' }}>{source.id}</code></span>
        {isInternal && source.installed_by && <span>installed_by: <code style={{ fontFamily: 'var(--font-mono)' }}>{source.installed_by}</code></span>}
        {isInternal && source.installed_at && <span>installed_at: {source.installed_at.slice(0, 10)}</span>}
        {isInternal && (source.known_producers ?? []).length > 0 && (
          <span>producers: {source.known_producers!.join(', ')}</span>
        )}
      </div>

      <Field label="Name" error={errors.name}>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" aria-invalid={errors.name ? true : undefined} style={inputStyle(Boolean(errors.name))} />
      </Field>
      <Field label="Description">
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" style={inputStyle(false)} />
      </Field>

      {isInternal ? (
        <>
          <Field label="Status">
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" style={inputStyle(false)}>
              <option value="">(none)</option>
              {withCurrent(SOURCE_STATUSES, source.status).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Target tool" error={errors.target_tool}>
            <input type="text" value={targetTool} onChange={(e) => setTargetTool(e.target.value)} aria-label="Target tool" aria-invalid={errors.target_tool ? true : undefined} style={{ ...inputStyle(Boolean(errors.target_tool)), fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </Field>
          <Field label="Target file" error={errors.target_file}>
            <input type="text" value={targetFile} onChange={(e) => setTargetFile(e.target.value)} aria-label="Target file" aria-invalid={errors.target_file ? true : undefined} style={{ ...inputStyle(Boolean(errors.target_file)), fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </Field>
          <Field label="Endpoint" error={errors.endpoint}>
            <input type="text" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} aria-label="Endpoint" aria-invalid={errors.endpoint ? true : undefined} style={{ ...inputStyle(Boolean(errors.endpoint)), fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </Field>
          <Field label="Direction">
            <select value={direction} onChange={(e) => setDirection(e.target.value as 'inbound' | 'bidirectional')} aria-label="Direction" style={inputStyle(false)}>
              <option value="inbound">inbound</option>
              <option value="bidirectional">bidirectional</option>
            </select>
          </Field>
          {/* CTSK-366 schemaRef — the loop-closer. */}
          <Field label="Schema ref — document id">
            <input type="text" value={schemaRefDocId} onChange={(e) => setSchemaRefDocId(e.target.value)} aria-label="Schema ref document id" placeholder="doc_2026…" style={{ ...inputStyle(false), fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </Field>
          <Field label="Schema ref — URL" error={errors.schemaRefUrl}>
            <input type="text" value={schemaRefUrl} onChange={(e) => setSchemaRefUrl(e.target.value)} aria-label="Schema ref URL" aria-invalid={errors.schemaRefUrl ? true : undefined} placeholder="https://…" style={{ ...inputStyle(Boolean(errors.schemaRefUrl)), fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </Field>
          <Field label="Schema ref — label">
            <input type="text" value={schemaRefLabel} onChange={(e) => setSchemaRefLabel(e.target.value)} aria-label="Schema ref label" placeholder="Schema" style={inputStyle(false)} />
          </Field>
        </>
      ) : (
        <>
          <Field label="Type" error={errors.type}>
            <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Type" style={inputStyle(Boolean(errors.type))}>
              {type.trim().length === 0 && <option value="">(pick one)</option>}
              {withCurrent(SOURCE_TYPES, source.type).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Status" error={errors.status}>
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" style={inputStyle(Boolean(errors.status))}>
              {status.trim().length === 0 && <option value="">(pick one)</option>}
              {withCurrent(SOURCE_STATUSES, source.status).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="URL">
            <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="URL" style={{ ...inputStyle(false), fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </Field>
          <Field label="Owner name">
            <input type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} aria-label="Owner name" style={inputStyle(false)} />
          </Field>
          <Field label="Owner email">
            <input type="text" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} aria-label="Owner email" style={inputStyle(false)} />
          </Field>
          <Field label="Update frequency">
            <select value={updateFrequency} onChange={(e) => setUpdateFrequency(e.target.value)} aria-label="Update frequency" style={inputStyle(false)}>
              <option value="">(none)</option>
              {withCurrent(UPDATE_FREQUENCIES, source.update_frequency).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Subscription">
            <select value={subscription} onChange={(e) => setSubscription(e.target.value)} aria-label="Subscription" style={inputStyle(false)}>
              <option value="">(none)</option>
              {withCurrent(SUBSCRIPTIONS, source.subscription).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="License">
            <input type="text" value={license} onChange={(e) => setLicense(e.target.value)} aria-label="License" style={inputStyle(false)} />
          </Field>
          <Field label="Annual cost (USD)" error={errors.cost}>
            <input type="text" inputMode="decimal" value={costText} onChange={(e) => setCostText(e.target.value)} aria-label="Annual cost" aria-invalid={errors.cost ? true : undefined} style={inputStyle(Boolean(errors.cost))} />
          </Field>
          <Field label="Tools used (comma-separated)">
            <input type="text" value={toolsUsedText} onChange={(e) => setToolsUsedText(e.target.value)} aria-label="Tools used" style={inputStyle(false)} />
          </Field>
          <Field label="Last reviewed">
            <input type="date" value={lastReviewed} onChange={(e) => setLastReviewed(e.target.value)} aria-label="Last reviewed" style={inputStyle(false)} />
          </Field>
        </>
      )}

      <Field label="Tags (comma-separated)">
        <input type="text" value={tagsText} onChange={(e) => setTagsText(e.target.value)} aria-label="Tags" style={inputStyle(false)} />
      </Field>
      <Field label="Notes">
        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Notes" style={inputStyle(false)} />
      </Field>

      {serverError && (
        <div role="alert" style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--danger-primary, #B3261E)' }}>
          {serverError}
        </div>
      )}

      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{ padding: '5px 14px', fontSize: 12, border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-raised)', color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => { void save(); }}
          disabled={!valid || saving}
          style={{
            padding: '5px 14px',
            fontSize: 12,
            border: '1px solid var(--accent-primary)',
            borderRadius: 6,
            background: (!valid || saving) ? 'color-mix(in srgb, var(--accent-primary) 35%, var(--surface-raised))' : 'var(--accent-primary)',
            color: 'var(--accent-on-primary, #fff)',
            cursor: (!valid || saving) ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
      {children}
      {error && (
        <span role="alert" style={{ fontSize: 11, textTransform: 'none', letterSpacing: 'normal', color: 'var(--danger-primary, #B3261E)' }}>
          {error}
        </span>
      )}
    </label>
  );
}

function inputStyle(invalid: boolean): React.CSSProperties {
  return {
    padding: '6px 8px',
    fontSize: 13,
    border: `1px solid ${invalid ? 'var(--danger-primary, #B3261E)' : 'var(--border-strong)'}`,
    borderRadius: 6,
    background: 'var(--surface-base, var(--surface-raised))',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    textTransform: 'none',
    letterSpacing: 'normal',
  };
}
